import { useMemo } from 'react';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { UnitsEnum } from '../../../services/entities/impl/blfApi/apiConstants';
import { useGetBlocksQuery } from '../../../services/entities/impl/blfApi/endpoints/blocks';
import {
  useAddCustomerMutation,
  useDeleteCustomerMutation,
  useGetCustomersQuery,
  useUpdateCustomerMutation,
} from '../../../services/entities/impl/blfApi/endpoints/customers';
import { useGetRanchesQuery } from '../../../services/entities/impl/blfApi/endpoints/ranches';
import { useGetAllScansQuery } from '../../../services/entities/impl/blfApi/endpoints/scans';
import { useGetUserMeQuery } from '../../../services/entities/impl/blfApi/endpoints/users';
import {
  Customer,
  CustomerSchema,
} from '../../../services/entities/impl/blfApi/schemas/customer';
import { UserRolesEnum } from '../../../services/entities/impl/blfApi/schemas/user';
import { useLanguageOptions } from '../../../services/translation/hooks/useLanguageOptions';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import {
  OnboardFormStepEnum,
  selectCurrOnboardCustomerID,
  setCurrOnboardCustomerID,
} from '../onboardingSlice';
import { DeleteWarningBase } from './components/DeleteWarningBase';
import { GenericForm, GenericFormProps } from './generic/GenericForm';
import { ExtraValidationFunction } from './generic/context/CRUDContext';
import { FormFieldType } from './generic/crud/components/FormFieldType';
import { checkFieldUniqueness } from './generic/crud/utils/checkFieldUniqueness';
import { extractIDFromResponse } from './generic/crud/utils/extractIDFromResponse';
import { toSelectOptions } from './generic/crud/utils/toSelectOptions';

const CustomerSchemaIn = yup.object().shape({
  name: yup.string().required('Name is required'),
  unitDisplay: yup
    .mixed<UnitsEnum>()
    .oneOf(Object.values(UnitsEnum), 'Invalid unit display')
    .required('Unit display is required'),
  language: yup.string().default('en'),
  shortCode: yup
    .string()
    .matches(/^[a-zA-Z0-9]*$/, 'Short code must be alphanumeric')
    .max(5, 'Short code must be at most 5 characters')
    .required('Short code is required'),
});

type CustomerSchemaIn = yup.InferType<typeof CustomerSchemaIn>;

const useGetCurrOnboardCustomer = () => {
  const currOnboardCustomerID = useAppSelector(selectCurrOnboardCustomerID);
  const {
    currentData: currCustomer,
    isLoading,
    isFetching,
  } = useGetCustomersQuery(undefined, {
    skip: !currOnboardCustomerID,
    selectFromResult: ({ data, isLoading, isFetching }) => {
      return {
        currentData: data?.find(
          (customer) => customer.id === currOnboardCustomerID
        ),
        isLoading,
        isFetching,
      };
    },
  });

  return {
    currentData: currOnboardCustomerID ? currCustomer || null : null,
    isLoading,
    isFetching,
  };
};

export const CustomerForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const [handleAdd] = useAddCustomerMutation();
  const [handleUpdate] = useUpdateCustomerMutation();
  const [handleDelete] = useDeleteCustomerMutation();

  const languagesOptions = useLanguageOptions();
  const unitsOptions = toSelectOptions(UnitsEnum);
  const { currentData: customers } = useGetCustomersQuery();
  const { currentData: ranches } = useGetRanchesQuery();
  const { currentData: blocks } = useGetBlocksQuery();
  const { currentData: scans } = useGetAllScansQuery();
  const { currentData: authorizedUser } = useGetUserMeQuery();
  const { currentData: currEntity } = useGetCurrOnboardCustomer();
  const { t } = useTranslation();

  const deleteWarningProps = useMemo(() => {
    const warningText = t(
      'By deleting customer all related items will be deleted as well.',
      { ns: 'common' }
    );

    if (!currEntity || !scans || !ranches || !blocks) {
      return { warningText };
    }

    const relatedRanches = ranches.filter(
      (ranch) => ranch.customerID === currEntity?.id
    );
    const relatedBlocks = blocks.filter((block) =>
      relatedRanches.map((ranch) => ranch.id).includes(block.ranchID)
    );
    const relatedScans = scans.filter((scan) =>
      relatedBlocks.map((block) => block.id).includes(scan.blockID)
    );

    return {
      warningText,
      warnedChilds: {
        Ranches: {
          items: relatedRanches,
        },
        Blocks: {
          items: relatedBlocks,
        },
        Scans: {
          items: relatedScans,
        },
      },
    };
  }, [scans, currEntity]);

  const extraValidation: ExtraValidationFunction<CustomerSchemaIn> = (
    values
  ) => {
    return checkFieldUniqueness({
      dataset: customers,
      fieldsToCheck: ['name', 'shortCode'],
      values: values,
      currentItemId: currEntity?.id,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmitSuccess = async (res: any) => {
    const id = extractIDFromResponse(res);
    if (id) {
      dispatch(setCurrOnboardCustomerID(id));
    }
  };

  const crudConfig: GenericFormProps<CustomerSchemaIn, Customer> = {
    handleAdd,
    handleUpdate,
    handleDelete,
    getCurrEntityHook: useGetCurrOnboardCustomer,
    yupSchemaIn: CustomerSchemaIn,
    yupSchemaOut: CustomerSchema,
    extraValidation,
    onSubmitSuccess,
    slots: {
      [OnboardFormStepEnum.Add]: {
        props: {
          gridConfig: {
            numberOfColumns: 2,
          },
          fieldsConfig: {
            name: {
              type: FormFieldType.TextField,
              label: t('Name', { ns: 'common' }),
              initialValue: '',
            },
            shortCode: {
              type: FormFieldType.TextField,
              label: t('Short Code', { ns: 'common' }),
              initialValue: '',
            },
            unitDisplay: {
              type: FormFieldType.Select,
              label: t('Unit Display', { ns: 'common' }),
              initialValue: UnitsEnum.imperial,
              config: {
                items: unitsOptions,
              },
            },
            language: {
              type: FormFieldType.Select,
              label: t('Language', { ns: 'common' }),
              initialValue: 'en',
              config: {
                items: languagesOptions,
              },
            },
          },
        },
      },
      [OnboardFormStepEnum.Edit]: {
        props: {
          gridConfig: {
            numberOfColumns: 2,
          },
          hiddenFields: ['id', 'fromDate', 'thruDate'],
          fieldsConfig: {
            id: {
              // hidden by hiddenFields config prop but needed to set the edit id
              type: FormFieldType.TextField,
              label: 'ID',
            },
            name: {
              type: FormFieldType.TextField,
              label: t('Name', { ns: 'common' }),
              initialValue: '',
            },
            shortCode: {
              type: FormFieldType.TextField,
              label: t('Short Code', { ns: 'common' }),
              initialValue: '',
            },
            unitDisplay: {
              type: FormFieldType.Select,
              label: t('Unit Display', { ns: 'common' }),
              initialValue: UnitsEnum.imperial,
              config: {
                items: unitsOptions,
              },
            },
            language: {
              type: FormFieldType.Select,
              label: t('Language', { ns: 'common' }),
              config: {
                items: languagesOptions,
              },
            },
          },
        },
      },
      [OnboardFormStepEnum.View]: {
        props: {
          hiddenFields: ['id', 'fromDate', 'thruDate'],
          fieldsConfig: {
            id: {
              type: FormFieldType.TextDisplay,
              label: t('ID'),
              initialValue: '',
            },
            name: {
              type: FormFieldType.TextDisplay,
              label: t('Name', { ns: 'common' }),
            },
            shortCode: {
              type: FormFieldType.TextDisplay,
              label: t('Short Code', { ns: 'common' }),
            },
            unitDisplay: {
              type: FormFieldType.TextDisplay,
              label: t('Unit Display', { ns: 'common' }),
              config: {
                valueGetter: (field) =>
                  unitsOptions.find((r) => r.value === field.value)?.label ||
                  'Units Not Found',
              },
            },
            language: {
              type: FormFieldType.TextDisplay,
              label: t('Language', { ns: 'common' }),
              config: {
                valueGetter: (field) =>
                  languagesOptions.find((l) => l.value === field.value)
                    ?.label || 'Language Not Found',
              },
            },
          },
        },
      },
      [OnboardFormStepEnum.Delete]: {
        props: {
          submitCondition: () =>
            !authorizedUser?.roles
              .map((role) => role.name)
              .includes(UserRolesEnum.root),
          WarningElement: <DeleteWarningBase {...deleteWarningProps} />,
          hiddenFields: ['id', 'fromDate', 'thruDate'],
          fieldsConfig: {
            id: {
              type: FormFieldType.TextDisplay,
              label: t('ID'),
              initialValue: '',
            },
            name: {
              type: FormFieldType.TextDisplay,
              label: t('Name', { ns: 'common' }),
            },
            shortCode: {
              type: FormFieldType.TextDisplay,
              label: t('Short Code', { ns: 'common' }),
            },
            unitDisplay: {
              type: FormFieldType.TextDisplay,
              label: t('Unit Display', { ns: 'common' }),
              config: {
                valueGetter: (field) =>
                  unitsOptions.find((r) => r.value === field.value)?.label ||
                  'Units Not Found',
              },
            },
            language: {
              type: FormFieldType.TextDisplay,
              label: t('Language', { ns: 'common' }),
              config: {
                valueGetter: (field) =>
                  languagesOptions.find((l) => l.value === field.value)
                    ?.label || 'Language Not Found',
              },
            },
          },
        },
      },
    },
  };
  return <GenericForm {...crudConfig} />;
};
